import { trackException } from '../../applicationInsights';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useSnackbar, VariantType } from 'notistack';
import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { AuthContext } from '../Authorisation/AuthContext';
import ReturnViewModel from './ReturnViewModel';
import { SystemLogContext } from './SystemLogContext';
import { Breakpoint } from '@mui/material';
import { buildTelemetryHeaders } from './DeviceTelemetry';

type ContextModel = {
    showMessage: (severity: VariantType, message: string) => void;
    post: (controller: string, action: string, data: any, callback?: (data: any) => void, signal?: AbortSignal) => Promise<void>;
    get: (controller: string, action: string, callback?: (data: any) => void, signal?: AbortSignal) => void;
    getChart: (controller: string, action: string, callback: (url: string) => void) => void;
    getPDF: (controller: string, action: string, fileName: string, openInNewTab: boolean) => void;
    postPDF: (controller: string, action: string, fileName: string, openInNewTab: boolean, postData: object) => void;
    getCSV: (controller: string, action: string, fileName: string) => void;
    getFile: (fileName: string, fileExtension: string) => void;
    postCSV: (controller: string, action: string, fileName: string, postData: object) => void;
    openDialog: (content: ReactNode, actions: DialogAction[], title?: string, closeLabel?: string, size?: Breakpoint) => void;
    loading: boolean;
    getUserSetting: (settingName: string, callback: (value: string | null) => void) => void;
    setUserSetting: (settingName: string, value: string, callback?: (success: boolean) => void) => void;
    version: number;
    setVersion: (version: number) => void;
};

export const APIContext = createContext<ContextModel>({
    showMessage: () => { throw Error('Cannot use a context without a provider'); },
    post: () => { throw Error('Cannot use a context without a provider'); },
    get: () => { throw Error('Cannot use a context without a provider'); },
    getChart: () => { throw Error('Cannot use a context without a provider'); },
    getPDF: () => { throw Error('Cannot use a context without a provider'); },
    postPDF: () => { throw Error('Cannot use a context without a provider'); },
    getCSV: () => { throw Error('Cannot use a context without a provider'); },
    postCSV: () => { throw Error('Cannot use a context without a provider'); },
    getFile: () => { throw Error('Cannot use a context without a provider'); },
    openDialog: () => { throw Error('Cannot use a context without a provider'); },
    loading: false,
    getUserSetting: () => { throw Error('Cannot use a context without a provider'); },
    setUserSetting: () => { throw Error('Cannot use a context without a provider'); },
    version: -1,
    setVersion: () => { throw Error('Cannot use a context without a provider'); }
});

export default function APIContextProvider({ children }: React.PropsWithChildren<{}>) {
    const { enqueueSnackbar } = useSnackbar();
    const [loadingCalls, setLoadingCalls] = useState<string[]>([]);
    const [open, setOpen] = useState(false);
    const [dialogTitle, setDialogTitle] = useState<string | undefined>(undefined);
    const [dialogContent, setDialogContent] = useState<ReactNode | null>(null);
    const [dialogCloseLabel, setDialogCloseLabel] = useState<string | undefined>(undefined);
    const [dialogActions, setDialogActions] = useState<DialogAction[]>([]);
    const { refreshCredentials } = useContext(AuthContext);
    const { logAPICall } = useContext(SystemLogContext);
    const [version, setVersion] = useState(() => {
        const date = new Date().toDateString();
        const saved = localStorage.getItem(`version-${date}`);
        return saved ? parseInt(saved, 10) : -1;
    });
    const [dialogSize, setDialogSize] = useState<Breakpoint>("sm");

    const loading = useMemo(() => loadingCalls.length > 0, [loadingCalls]);

    const showMessage = (severity: VariantType, message: string) => {
        enqueueSnackbar(message, {
            anchorOrigin: {
                vertical: 'top',
                horizontal: 'right',
            },
            variant: severity
        });
        console.log(severity + ": " + message);
    }

    const mergeHeaders = (base?: Record<string, string>) => {
        return { ...buildTelemetryHeaders(), ...(base ?? {}) };
    };

    const validationFieldLabel = (path: string) => {
        const field = path.replace(/^\$\.?/, '').split('.').pop() ?? path;
        return field
            .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
            .replace(/^./, character => character.toUpperCase());
    };

    const validationMessage = (path: string, message: string) => {
        const label = validationFieldLabel(path);
        if (/System\.(?:Nullable`1\[System\.)?(?:Int16|Int32|Int64)/.test(message)) {
            return `${label} must be a whole number.`;
        }
        if (/System\.(?:Nullable`1\[System\.)?(?:Single|Double|Decimal)/.test(message)) {
            return `${label} must be a valid number.`;
        }
        return `${label}: ${message}`;
    };

    const showValidationProblem = (result: any) => {
        const errors = result?.errors as Record<string, string[]> | undefined;
        if (!errors) return false;

        Object.entries(errors).forEach(([path, messages]) => {
            messages.forEach(message => showMessage('error', validationMessage(path, message)));
        });
        return true;
    };

    const post = async (controller: string, action: string, data: any, callback?: (data: any) => void, signal?: AbortSignal) => {
        const apiPath = `${controller}/${action}`;
        const jsonViewModel = JSON.stringify(data);
        setLoadingCalls(prev => [...prev, apiPath]);
        const isFormData = data instanceof FormData;
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'POST',
                credentials: 'include',
                headers: isFormData
                    ? mergeHeaders()
                    : mergeHeaders({ 'Content-Type': 'application/json', 'x-data-version': version.toString() }),
                body: isFormData ? data : jsonViewModel,
                signal: signal
            });
            if (response.status === 401) {
                refreshCredentials(() => post(controller, action, data, callback));
                return;
            }
            const result: ReturnViewModel = await response.json();
            if (!response.ok) {
                if (!showValidationProblem(result)) {
                    showMessage('error', (result as any)?.title ?? `Request failed (${response.status})`);
                }
                return;
            }
            if (!result.success) {
                result.messages.forEach(msg => {
                    showMessage('error', msg);
                    console.error(`${apiPath}: ${msg}`);
                });
                return;
            }
            callback?.(result.value);
        } catch (error: any) {
            console.error(apiPath + ': ' + error);
            trackException(error);
        } finally {
            setLoadingCalls(prev => prev.filter(f => f !== apiPath));
        }
    }

    const get = async (controller: string, action: string, callback?: (data: any) => void, signal?: AbortSignal) => {
        const apiPath = `${controller}/${action}`;
        setLoadingCalls(prev => [...prev, apiPath]);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'GET',
                credentials: 'include',
                headers: mergeHeaders({ 'Content-Type': 'application/json', 'x-data-version': version.toString() }),
                signal
            });
            if (response.status === 401) {
                refreshCredentials(() => get(controller, action, callback, signal));
                return;
            }
            const result: ReturnViewModel = await response.json();
            if (!result.success) {
                result.messages.forEach(msg => {
                    showMessage('error', msg);
                    console.error(`${apiPath}: ${msg}`);
                });
                return;
            }
            callback?.(result.value);
        } catch (error: any) {
            if (error.name === "AbortError") return;
            console.error(apiPath + ': ' + error);
            trackException(error);
        } finally {
            setLoadingCalls(prev => prev.filter(f => f !== apiPath));
        }
    }

    const getChart = async (controller: string, action: string, callback: (url: string) => void) => {
        const apiPath = `${controller}/${action}`;
        const jsonViewModel = action.substring(action.indexOf('?') + 1);
        const start = performance.now();
        let jsonResult = '';
        setLoadingCalls(prev => [...prev, apiPath]);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'GET',
                credentials: 'include',
                headers: mergeHeaders({ 'Content-Type': 'application/json', 'x-data-version': version.toString() })
            });
            if (response.status === 401) {
                refreshCredentials(() => getChart(controller, action, callback));
                return;
            }
            const blob = await response.blob();
            const objectURL = URL.createObjectURL(blob);
            jsonResult = JSON.stringify(objectURL);
            callback(objectURL);
        } catch (error: any) {
            jsonResult = JSON.stringify(error);
            console.error(apiPath + ': ' + error);
            trackException(error);
        } finally {
            const end = performance.now();
            logAPICall('LogCount', apiPath, jsonViewModel, jsonResult, end - start);
            setLoadingCalls(prev => prev.filter(f => f !== apiPath));
        }
    }

    const getFile = async (fileName: string, fileExtension: string) => {
        const apiPath = `LegacySystem/GetFileLink?fileName=${fileName}&fileExtension=${fileExtension}&`;
        setLoadingCalls(prev => [...prev, apiPath]);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'GET',
                credentials: 'include',
                headers: mergeHeaders({ 'Content-Type': 'application/json; charset=utf-8' })
            });
            if (response.status === 401) {
                refreshCredentials(() => getFile(fileName, fileExtension));
                return;
            }
            const result: ReturnViewModel = await response.json();
            if (!result.success) {
                result.messages.forEach(msg => {
                    showMessage('error', msg);
                    console.error(`${apiPath}: ${msg}`);
                });
                return;
            }
            const url = result.value;
            window.open(url);
        } catch (error: any) {
            console.error(apiPath + ': ' + error);
            trackException(error);
        } finally {
            setLoadingCalls(prev => prev.filter(f => f !== apiPath));
        }
    }

    const getPDF = async (controller: string, action: string, fileName: string, openInNewTab: boolean) => {
        const apiPath = `${controller}/${action}`;
        const jsonViewModel = action.substring(action.indexOf('?') + 1);
        const start = performance.now();
        let jsonResult = '';
        setLoadingCalls(prev => [...prev, apiPath]);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'GET',
                credentials: 'include',
                headers: mergeHeaders( { 'Content-Type': 'application/json; charset=utf-8', 'x-data-version': version.toString() })
            });
            if (response.status === 401) {
                refreshCredentials(() => getPDF(controller, action, fileName, openInNewTab));
                return;
            }
            const blob = await response.blob();
            const objectURL = URL.createObjectURL(blob);
            jsonResult = JSON.stringify(objectURL);
            if (openInNewTab) {
                const newTab = window.open(objectURL, '_blank');
                if (newTab) newTab.onload = () => newTab.document.title = fileName;
            } else {
                const a = document.createElement('a');
                a.href = objectURL;
                a.download = `${fileName}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(objectURL);
            }
        } catch (error: any) {
            jsonResult = JSON.stringify(error);
            console.error(apiPath + ': ' + error);
            trackException(error);
        } finally {
            const end = performance.now();
            logAPICall('LogCount', apiPath, jsonViewModel, jsonResult, end - start);
            setLoadingCalls(prev => prev.filter(f => f !== apiPath));
        }
    }

    const postPDF = async (controller: string, action: string, fileName: string, openInNewTab: boolean, postData: object) => {
        const apiPath = `${controller}/${action}`;
        const jsonViewModel = JSON.stringify(postData);
        const start = performance.now();
        let jsonResult = '';
        setLoadingCalls(prev => [...prev, apiPath]);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'POST',
                credentials: 'include',
                headers: mergeHeaders( { 'Content-Type': 'application/json; charset=utf-8', 'x-data-version': version.toString() }),
                body: JSON.stringify(postData)
            });
            if (response.status === 401) {
                refreshCredentials(() => postPDF(controller, action, fileName, openInNewTab, postData));
                return;
            }
            const blob = await response.blob();
            const objectURL = URL.createObjectURL(blob);
            jsonResult = JSON.stringify(objectURL);
            if (openInNewTab) {
                const newTab = window.open(objectURL, '_blank');
                if (newTab) newTab.onload = () => (newTab.document.title = fileName);
            } else {
                const a = document.createElement('a');
                a.href = objectURL;
                a.download = `${fileName}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(objectURL);
            }
        } catch (error: any) {
            jsonResult = JSON.stringify(error);
            console.error(apiPath + ': ' + error);
            trackException(error);
        } finally {
            const end = performance.now();
            logAPICall('LogCount', apiPath, jsonViewModel, jsonResult, end - start);
            setLoadingCalls(prev => prev.filter(f => f !== apiPath));
        }
    };

    const getCSV = async (controller: string, action: string, fileName: string) => {
        const apiPath = `${controller}/${action}`;
        const jsonViewModel = action.substring(action.indexOf('?') + 1);
        const start = performance.now();
        let jsonResult = '';
        setLoadingCalls(prev => [...prev, apiPath]);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'GET',
                credentials: 'include',
                headers: mergeHeaders( { 'Content-Type': 'application/json; charset=utf-8', 'x-data-version': version.toString() })
            });
            if (response.status === 401) {
                refreshCredentials(() => getCSV(controller, action, fileName));
                return;
            }
            const blob = await response.blob();
            const objectURL = URL.createObjectURL(blob);
            jsonResult = JSON.stringify(objectURL);
            const a = document.createElement('a');
            a.href = objectURL;
            a.download = `${fileName}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(objectURL);
        } catch (error: any) {
            jsonResult = JSON.stringify(error);
            console.error(apiPath + ': ' + error);
            trackException(error);
        } finally {
            const end = performance.now();
            logAPICall('LogCount', apiPath, jsonViewModel, jsonResult, end - start);
            setLoadingCalls(prev => prev.filter(f => f !== apiPath));
        }
    }

    const postCSV = async (controller: string, action: string, fileName: string, postData: object) => {
        const apiPath = `${controller}/${action}`;
        const jsonViewModel = JSON.stringify(postData);
        const start = performance.now();
        let jsonResult = '';
        setLoadingCalls(prev => [...prev, apiPath]);
        try {
            const response = await fetch(`api/${apiPath}`, {
                method: 'POST',
                credentials: 'include',
                headers: mergeHeaders( { 'Content-Type': 'application/json; charset=utf-8', 'x-data-version': version.toString() }),
                body: JSON.stringify(postData)
            });
            if (response.status === 401) {
                refreshCredentials(() => postCSV(controller, action, fileName, postData));
                return;
            }
            const blob = await response.blob();
            const objectURL = URL.createObjectURL(blob);
            jsonResult = JSON.stringify(objectURL);
            const a = document.createElement('a');
            a.href = objectURL;
            a.download = `${fileName}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(objectURL);
        } catch (error: any) {
            jsonResult = JSON.stringify(error);
            console.error(`${apiPath}: `, error);
            trackException(error);
        } finally {
            const end = performance.now();
            logAPICall('LogCount', apiPath, jsonViewModel, jsonResult, end - start);
            setLoadingCalls(prev => prev.filter(f => f !== apiPath));
        }
    };

    const getUserSetting = async (settingName: string, callback: (value: string | null) => void) => {
        get('UserSettings', `GetUserSetting?settingName=${encodeURIComponent(settingName)}`, callback);
    }

    const setUserSetting = async (settingName: string, value: string, callback?: (success: boolean) => void) => {
        get(
            'UserSettings',
            `SetUserSettings?settingName=${encodeURIComponent(settingName)}&value=${encodeURIComponent(value)}`,
            () => callback?.(true)
        );
    }

    const openDialog = (content: ReactNode, actions: DialogAction[], title?: string, closeLabel?: string, size?: Breakpoint) => {
        setOpen(true);
        setDialogContent(content);
        setDialogTitle(title);
        setDialogActions(actions);
        setDialogCloseLabel(closeLabel);
        setDialogSize(size ?? "sm");
    }

    const closeDialog = () => {
        setOpen(false);
        setDialogContent(null);
        setDialogTitle(undefined);
        setDialogActions([]);
        setDialogCloseLabel(undefined);
        setDialogSize("sm");
    }

    const setVersionCheck = (v: number) => {
        const date = new Date().toDateString();
        setVersion(v);
        localStorage.setItem(`version-${date}`, v.toString());
    }

    const context: ContextModel = {
        showMessage,
        post,
        getChart,
        getPDF,
        postPDF,
        get,
        getCSV,
        getFile,
        postCSV,
        openDialog,
        loading,
        getUserSetting,
        setUserSetting,
        version,
        setVersion: setVersionCheck
    }

    return (
        <APIContext.Provider value={context}>
            {children}
            <Dialog open={open} onClose={closeDialog} maxWidth={dialogSize} fullWidth>
                <DialogContent>
                    {dialogTitle && <DialogTitle>{dialogTitle}</DialogTitle>}
                    {dialogContent}
                    <DialogActions>
                        <Button onClick={closeDialog}>{dialogCloseLabel || "Close"}</Button>
                        {dialogActions.map((action, index) => (
                            <Button key={index} onClick={() => {
                                action.Action();
                                closeDialog();
                            }}>
                                {action.Text}
                            </Button>
                        ))}
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </APIContext.Provider>
    );
}

export class DialogAction {
    public Text: string = '';
    public Action: () => void = () => { };
    constructor(text: string, action: () => void) {
        this.Text = text;
        this.Action = action;
    }
}
